// components/promotion/SinglePromotionPopup.js
import React from 'react';
import { Link } from 'react-router-dom';
import { X, Gift, ExternalLink } from 'lucide-react';

function SinglePromotionPopup({ promotion, onClose }) {
  if (!promotion) return null;

  // Convert markdown-like syntax to HTML for display
  const formatDescriptionForDisplay = (text) => {
    if (!text) return '';
    
    return text
      // H2 headings - reduced spacing
      .replace(/^## (.+)$/gm, '<h2 class="text-lg font-bold text-red-800 mb-2 mt-2">$1</h2>')
      // H3 headings - reduced spacing
      .replace(/^### (.+)$/gm, '<h3 class="text-base font-bold text-red-800 mb-2 mt-2">$1</h3>')
      // Bold text
      .replace(/\*\*(.+?)\*\*/g, '<strong class="font-bold text-red-800">$1</strong>')
      // Bullet points
      .replace(/^• (.+)$/gm, '<div class="flex items-start mb-1"><span class="text-red-600 font-bold mr-2 mt-0.5">•</span><span class="text-gray-700">$1</span></div>')
      // Numbered points
      .replace(/^(\d+)\. (.+)$/gm, '<div class="flex items-start mb-1"><span class="text-red-600 font-bold mr-2 mt-0.5">$1.</span><span class="text-gray-700">$2</span></div>')
      // Convert remaining single newlines to spaces for paragraph flow
      .replace(/\n/g, ' ')
      // Clean up multiple spaces
      .replace(/\s+/g, ' ');
  };

  return (
    // Popup Container - No overlay here, overlay is in parent component
    <div className="bg-white rounded-lg shadow-xl overflow-hidden max-w-xl w-full max-h-[90vh] flex flex-col" 
         onClick={e => e.stopPropagation()} // Prevent clicks from propagating to the overlay
    >
      {/* Header with close button */}
      <div className="flex justify-between items-center p-4 border-b border-gray-200">
        <div className="flex items-center space-x-3">
          {/* Brand Logo */}
          <div className="h-10 w-10 rounded-full overflow-hidden border border-gray-200 flex-shrink-0">
            {promotion.brandLogo ? (
              <img 
                src={promotion.brandLogo} 
                alt="Brand logo" 
                className="h-full w-full object-contain"
              />
            ) : (
              <div className="h-full w-full flex items-center justify-center bg-blue-100">
                <span className="text-blue-500 font-bold text-lg">{promotion.brandName?.charAt(0) || "B"}</span>
              </div>
            )}
          </div>
          
          {/* Brand Name */}
          <div>
            {promotion.brandName && (
              <Link 
                to={`/brand/${promotion.brandSlug}`}
                className="text-base font-medium text-gray-800 hover:underline"
                onClick={onClose}
              >
                {promotion.brandName}
              </Link>
            )}
            
            {/* Category Badge moved here */}
            {promotion.category && (
              <span className="inline-block bg-blue-100 text-blue-700 text-xs px-2 py-1 rounded-full ml-2">
                {promotion.category}
              </span>
            )}
          </div>
        </div>
        
        {/* Close Button */}
        <button 
          onClick={onClose}
          className="text-gray-500 hover:text-gray-700 focus:outline-none"
          aria-label="Close"
        >
          <X size={24} />
        </button>
      </div>
      
      {/* Scrollable Content */}
      <div className="overflow-y-auto flex-grow">
        {/* Promotion Image */}
        <div className="bg-gray-100">
          {promotion.image ? (
            <img 
              src={promotion.image} 
              alt={promotion.title || "Promotion"} 
              className="w-full object-contain max-h-[50vh]"
            />
          ) : (
            <div className="flex items-center justify-center h-64 bg-blue-50">
              <Gift className="h-16 w-16 text-blue-300" />
            </div>
          )}
        </div>
        
        {/* Promotion Details */}
        <div className="p-6">
          {/* Title - Moved here from header */}
          {promotion.title && (
            <h2 className="text-2xl font-bold text-gray-800 leading-tight mb-4">{promotion.title}</h2>
          )}
          
          {/* Description */}
          <div className="prose max-w-none">
            {promotion.description ? (
              <div 
                className="text-gray-700"
                dangerouslySetInnerHTML={{ 
                  __html: formatDescriptionForDisplay(promotion.description) 
                }}
              />
            ) : (
              <p className="text-gray-500 italic">No description available</p>
            )}
          </div>
          
          {/* Terms & Conditions (if available) */}
          {promotion.terms && (
            <div className="mt-6 pt-4 border-t border-gray-200">
              <h3 className="text-sm font-medium text-gray-700 mb-2">Terms & Conditions</h3>
              <p className="text-sm text-gray-600">{promotion.terms}</p>
            </div>
          )}
        </div>
      </div>
      
      {/* Footer with actions */}
      <div className="border-t border-gray-200 p-4 bg-gray-50">
        <div className="flex flex-col sm:flex-row gap-3 justify-between">
          <Link
            to={`/brand/${promotion.brandSlug}`}
            className="w-full sm:w-auto px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white text-center font-medium rounded-lg transition-colors"
            onClick={onClose}
          >
            View Brand
          </Link>
          
          {promotion.brandWebsite && (
            <a 
              href={promotion.brandWebsite}
              target="_blank"
              rel="noopener noreferrer"
              className="w-full sm:w-auto flex items-center justify-center px-4 py-2 border border-gray-300 text-gray-700 font-medium rounded-lg hover:bg-gray-50 transition-colors"
            >
              <ExternalLink size={16} className="mr-2" />
              Visit Website
            </a>
          )}
        </div>
      </div>
    </div>
  );
}

export default SinglePromotionPopup;